
import React from 'react';
import { Copy, Download, RefreshCw, ExternalLink, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ArticleRecommendation } from '@/types/articleTypes';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface RecommendationsContainerProps {
  recommendations: ArticleRecommendation['recommendations'];
  handleCopyUrls: () => void;
  handleDownloadCsv: () => void;
  suggestMore: () => Promise<void>;
  isLoading: boolean;
  hasContextFilter?: boolean;
  contextFilter?: string;
}

const RecommendationsContainer: React.FC<RecommendationsContainerProps> = ({
  recommendations,
  handleCopyUrls,
  handleDownloadCsv,
  suggestMore,
  isLoading,
  hasContextFilter = false,
  contextFilter
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col">
          <h3 className="text-lg font-medium mb-1 flex items-center gap-2">
            <span>Recommended Articles for Internal Linking</span>
          </h3>
          {hasContextFilter && contextFilter && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Filter className="h-3 w-3" />
                Filter applied:
              </span>
              <Badge variant="outline" className="text-xs py-0 h-5 px-2 bg-secondary/50">
                {contextFilter}
              </Badge>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={handleCopyUrls} className="h-8">
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  <span>Copy All URLs</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy all recommendation URLs to clipboard</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={handleDownloadCsv} className="h-8">
                  <Download className="h-3.5 w-3.5 mr-1" />
                  <span>Download CSV</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Download recommendations as a CSV file</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={suggestMore} disabled={isLoading} className="h-8">
                  {isLoading ? (
                    <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  )}
                  <span>Suggest 5 More</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Get 5 more article recommendations</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
      <div className="space-y-6">
        {recommendations.map((rec, index) => (
          <div key={index} className="bg-secondary/30 border border-primary/10 rounded-lg overflow-hidden shadow-sm relative">
            <Badge
              className="absolute top-3 right-3 bg-primary/10 hover:bg-primary/15 text-primary border-none"
            >
              Match Score: {Math.round(rec.similarity * 100)}%
            </Badge>
            
            <div className="p-4">
              <h4 className="text-lg font-medium mb-3">
                <a 
                  href={rec.articleUrl} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="hover:text-primary flex items-start gap-1 group"
                >
                  {rec.articleTitle}
                  <ExternalLink className="h-4 w-4 mt-1 opacity-50 group-hover:opacity-100" />
                </a>
              </h4>
              
              <div className="text-sm text-muted-foreground mb-3">
                <a 
                  href={rec.articleUrl} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-primary/70 hover:text-primary break-all"
                >
                  {rec.articleUrl}
                </a>
              </div>
              
              <div className="flex flex-col gap-2">
                <div>
                  <span className="block text-sm font-medium text-muted-foreground">Why this article:</span>
                  <p className="text-sm mt-1">{rec.reasoning}</p>
                </div>
                
                {rec.keywords && rec.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {rec.keywords.map((keyword, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="bg-secondary/50 text-xs font-normal"
                      >
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {index > 0 && (
              <div 
                className={`absolute top-0 left-0 w-1 h-full 
                  ${rec.similarity > 0.9 ? 'bg-green-500' : 
                    rec.similarity > 0.8 ? 'bg-green-400' : 
                    rec.similarity > 0.7 ? 'bg-blue-400' : 'bg-blue-300'
                  }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecommendationsContainer;
